import { createFileRoute, Link } from "@tanstack/react-router";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const slug = "mcp-vs-rest-api-media";
const path = `/blog/${slug}`;

export const Route = createFileRoute(path)({
	component: Article,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		return {
			meta: [
				{
					title: "MCP vs REST APIs for AI Media Generation - AgentFramer",
				},
				{
					name: "description",
					content:
						"Side-by-side comparison: REST polling loops vs a single MCP tool call. Auth, discovery, latency, and when MCP is overkill.",
				},
				{
					property: "og:title",
					content: "MCP vs REST APIs for AI Media Generation",
				},
				{
					property: "og:description",
					content:
						"REST for backends, MCP for agents. Concrete numbers, real code, and the cases where MCP is overkill.",
				},
				{ property: "og:image", content: ogImageUrl(`blog-${slug}`) },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl(`blog-${slug}`) },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function Article() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground/60">April 2026</p>
				<h1 className="text-2xl font-medium tracking-tight">
					MCP vs REST APIs for AI media generation
				</h1>
				<p className="text-sm text-muted-foreground">
					One task, two surfaces. The same generation request takes about thirty
					lines over REST and four lines over MCP. Here is the honest tradeoff,
					with numbers.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<h2 className="text-base font-medium text-foreground pt-2">
					The same job, written twice
				</h2>
				<p>
					Generate one image. Get the URL. That is the whole task. AgentFramer
					exposes both a REST API and a hosted MCP server that share credits,
					storage, and workspaces, so the comparison is apples to apples.
				</p>
				<p>
					Over REST, the client owns the state machine. POST creates a job, then
					the client polls until the status flips to <code>succeeded</code>,
					then it reads the URL. Around thirty lines of TypeScript once you
					handle errors, backoff, and the case where the job fails halfway
					through.
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`// REST: POST -> poll -> read URL
const API = "https://api.agentframer.com/v1";
const KEY = process.env.AGENTFRAMER_API_KEY!;

async function generate(prompt: string): Promise<string> {
  const create = await fetch(\`\${API}/generations\`, {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${KEY}\`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ type: "image", prompt, model: "flux-1.1-pro" }),
  });
  if (!create.ok) throw new Error(\`create \${create.status}\`);
  const { id } = (await create.json()) as { id: string };

  for (let i = 0; i < 60; i++) {
    await new Promise((r) => setTimeout(r, 1000 + i * 250));
    const poll = await fetch(\`\${API}/generations/\${id}\`, {
      headers: { Authorization: \`Bearer \${KEY}\` },
    });
    if (!poll.ok) throw new Error(\`poll \${poll.status}\`);
    const job = (await poll.json()) as {
      status: "queued" | "running" | "succeeded" | "failed";
      url?: string;
      error?: string;
    };
    if (job.status === "succeeded" && job.url) return job.url;
    if (job.status === "failed") throw new Error(job.error ?? "failed");
  }
  throw new Error("timeout");
}`}</pre>
				<p>
					Over MCP, the agent calls a tool. The server holds the job, streams
					progress notifications back, and resolves with a structured result
					that already contains the URL. No polling loop, no job IDs to track,
					no retry math.
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`// MCP: one tool call, URL in the result
const result = await client.callTool({
  name: "generate_image",
  arguments: { prompt, model: "flux-1.1-pro" },
});
const url = result.structuredContent?.url as string;`}</pre>

				<h2 className="text-base font-medium text-foreground pt-2">
					Auth: long-lived key vs short-lived token
				</h2>
				<p>
					REST authenticates with an API key the caller stores in an env var.
					The key has full account scope, no expiry, and lives wherever the
					caller runs. That is fine for a backend behind your firewall. It is
					not fine inside an editor or chat UI where the model can see the
					environment.
				</p>
				<p>
					MCP delegates auth to the host. AgentFramer's MCP server speaks OAuth
					2.1 with PKCE: the host opens a browser, the user grants scopes
					(workspace, generation, billing), and the host receives an access
					token that expires in 60 minutes plus a refresh token. The agent
					process never holds a long-lived secret. Tokens stay in the host
					keychain, not in the model's context.
				</p>
				<p>
					Concretely, that flow is four steps the user sees once: click connect,
					sign in, approve scopes, return to the editor. After that, refresh
					happens silently. See{" "}
					<Link
						to="/security"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						the security model
					</Link>{" "}
					for the scope list.
				</p>
				<p>
					The blast radius is different too. A leaked API key from a CI log
					grants whatever scopes you provisioned it with until you rotate. A
					leaked MCP access token expires on its own in under an hour and was
					never written to disk in the first place. For a tool that spends
					credits, that gap is the difference between a panicked rotation and a
					boring incident report.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Discovery: 200 KB of OpenAPI vs streamed tool list
				</h2>
				<p>
					A REST-only setup forces the model to learn endpoints from
					documentation. The simplest path is to drop the OpenAPI spec into the
					system prompt. AgentFramer's spec is around 200 KB of JSON, roughly 45
					thousand tokens, before you have asked it to generate anything.
					Truncating helps but means the model now has a partial map of the API.
				</p>
				<p>
					MCP solves this at the protocol level. On connect, the server answers{" "}
					<code>tools/list</code> with twelve tools and their JSON Schemas:{" "}
					<code>generate_image</code>, <code>generate_video</code>,{" "}
					<code>generate_audio</code>, <code>get_generation</code>,{" "}
					<code>list_recent_generations</code>, <code>get_credits</code>,{" "}
					<code>list_workspaces</code>, <code>switch_workspace</code>,{" "}
					<code>top_up_balance</code>, <code>list_members</code>,{" "}
					<code>invite_team_member</code>, and <code>submit_feedback</code>. The
					host registers them with the model directly. The schemas count as tool
					definitions, not free-text context, and the host can hide tools the
					user did not authorize. See the{" "}
					<Link
						to="/help/mcp-tools"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						full tool reference
					</Link>{" "}
					for argument shapes.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Latency: in the noise
				</h2>
				<p>
					Both surfaces are HTTP. MCP rides on stdio, SSE, or streamable HTTP;
					all three terminate at the same generation pipeline. The wire-level
					overhead is one extra envelope per call, sub-millisecond. The model
					itself can spend a second or two reasoning before issuing a tool call,
					which dwarfs anything either protocol adds.
				</p>
				<p>
					Where the experience diverges is perceived latency. REST polling at
					one-second intervals returns the URL on a one-second granularity. MCP
					progress notifications arrive when the server sends them, typically
					every few hundred milliseconds while the image renders. Same total
					time, smoother feedback.
				</p>
				<p>
					The hidden cost on the REST side is the polling itself. A 12-second
					image render at one-second intervals burns 12 round trips that mostly
					return <code>running</code>. None of those count against rate limits
					in any meaningful way, but they do count against your egress and your
					patience when you scale to thousands of concurrent jobs. MCP folds all
					of that into one open stream.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Resources: URIs the model fetches on demand
				</h2>
				<p>
					Generated images, videos, and audio are megabytes. You do not want
					them in the model's context. MCP has a first-class resources concept:
					the server returns a URI like{" "}
					<code>agentframer://generations/abc/image.png</code>, the host caches
					it, and the model only fetches the bytes when it needs them (for
					example, to reason about the result or hand it to another tool). REST
					can mimic this by returning a signed URL, but the discipline is on the
					client; with MCP it is on the protocol.
				</p>
				<p>
					The same mechanism makes <code>list_recent_generations</code> cheap.
					Calling it returns metadata and resource URIs, not pixel data. The
					model can browse a workspace's history, pick the right asset by prompt
					or timestamp, and only pull bytes when it has decided to act on one. A
					REST equivalent forces the client to either over-fetch or reimplement
					that two-stage pattern by hand.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					When MCP is overkill
				</h2>
				<p>
					MCP exists because models call tools differently than humans do. If no
					model is in the loop, you do not need it. Picking MCP for a
					non-agentic workload buys you a stateful session, an OAuth dance, and
					a transport abstraction in exchange for nothing.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">CI jobs.</strong> A GitHub
						Action that renders preview thumbnails on every PR has a known
						input, a known output, and no agent. POST and poll. Done.
					</li>
					<li>
						<strong className="text-foreground">Batch backends.</strong>{" "}
						Generating 50,000 product images from a CSV is a queue worker
						problem. REST scales horizontally with idempotency keys. MCP adds a
						stateful session you do not need.
					</li>
					<li>
						<strong className="text-foreground">Webhooks and triggers.</strong>{" "}
						When a row lands in your database and you generate an asset for it,
						the caller is your application code. Use REST.
					</li>
					<li>
						<strong className="text-foreground">
							Anything with a fixed prompt template.
						</strong>{" "}
						If the prompt is built deterministically from data, the schema
						discovery and tool selection that MCP buys you are wasted. A typed
						REST client is simpler to reason about and easier to monitor.
					</li>
				</ul>

				<h2 className="text-base font-medium text-foreground pt-2">
					Mixed-mode is the real answer
				</h2>
				<p>
					Most teams running AgentFramer in production use both. Backend
					services hit REST for cron, batch, and webhook work. Designers and
					engineers in Claude Code, Cursor, or Claude Desktop hit MCP for ad hoc
					generation, iteration, and review. They share the same workspace, the
					same credit pool, and the same storage bucket; only the wire format
					differs.
				</p>
				<p>
					Concretely: your nightly job that re-renders product hero images runs
					over REST with a service-account key scoped to one workspace. Your
					marketing team's editor uses MCP with OAuth, picks up the same
					credits, and can hand finished assets back to the same storage URLs
					the cron job reads from. No duplication.
				</p>
				<p>
					The transport you pick on the MCP side depends on where the host runs.
					Local hosts like Claude Desktop and Cursor speak <code>stdio</code> to
					a process they spawn. Remote hosts and web UIs use <code>SSE</code> or{" "}
					<code>streamable HTTP</code> against the hosted server. AgentFramer
					ships all three; the tool surface is the same on each. The full
					catalog of models you can call through either protocol lives on the{" "}
					<Link
						to="/models"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						models page
					</Link>
					.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					How to pick, in one paragraph
				</h2>
				<p>
					If the caller is a model running inside a host the user trusts, use
					MCP. If the caller is your own code running on your own
					infrastructure, use REST. If both, run both: AgentFramer is built for
					that. Start with the{" "}
					<Link
						to="/help/quick-start"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						quick start
					</Link>
					, wire whichever surface fits the call site, and keep the other one in
					your back pocket for when the requirement flips.
				</p>
			</div>
		</article>
	);
}
